<script lang="ts" setup>
import {ExtendedGroup, ExtendedProject, Group, Role, User} from "@/types";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import InfoCard from "@/components/cards/InfoCard.vue";
import GroupsTable from "@/Pages/projects/groups/components/GroupsTable.vue";
import {Head, router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {Ref, ref} from "vue";
import {throttle} from "lodash";
import axios from "axios";
import AssignUserDialog from "@/Pages/projects/groups/components/AssignUserDialog.vue";
import {InertiaForm} from "@inertiajs/vue3";

interface Props {
    project: ExtendedProject,
    role: Role,
    groups: Array<ExtendedGroup>,
}

interface Form {
    user: User | null;
    group_id: number | null;
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})
const search = ref('')
const isDialogOpen = ref(false)
const isLoading = ref(false)
// @ts-ignore
const form: InertiaForm<Form> = useForm({
    user: null,
    group_id: null
})
const users = ref([])
const selectedGroup: Ref<any> = ref(null)
const submit = () => {
    if (form.user) {
        form.patch(route('projects.users.update', {project: props.project.slug, user: form.user.name}), {
            preserveScroll: true,
            onSuccess: () => {
                isDialogOpen.value = false
                form.reset()
                router.reload({
                    only: ['groups']
                })
            }
        })
    }
}

const addMember = (group: any) => {
    selectedGroup.value = group
    isDialogOpen.value = true
    form.group_id = group.id
    isDialogOpen.value = true
}

const query = throttle((search: string) => {
    if (!search) {
        users.value = []
        return
    }

    isLoading.value = true

    if (search.length < 1) {
        users.value = []
        isLoading.value = false
        return
    }

    axios.get(
        route('web_api.projects.users.search', {project: props.project.slug}),
        {
            params: {
                search: search,
                group_id: selectedGroup.value.id,
            }
        }
    ).then(({ data }) => {
        users.value = data.data
    }).catch(() => {
        users.value = []
    }).finally(() => {
        isLoading.value = false
    })
}, 150)
</script>

<template>
    <Head title="Groups" />
    <project-layout
        :role="props.role"
        :project="props.project"
    >
        <assign-user-dialog
            v-model="form"
            v-model:search-input="search"
            v-model:is-dialog-open="isDialogOpen"
            :is-loading="isLoading"
            :group="selectedGroup ? selectedGroup.name : 'Unknown'"
            :items="users"
            @update:search-input="query($event)"
            @click:confirm="submit"
        />
        <h1 class="font-weight-medium text-h5 mt-5">
            Who has access
        </h1>
        <v-divider class="my-5" />
        <v-container>
            <v-card
                :flat="true"
                class="mb-5"
            >
                <v-row>
                    <v-col
                        cols="4"
                    >
                        <info-card
                            title="GROUPS"
                            icon="mdi-account-multiple-outline"
                        >
                            <p class="black--text">
                                <b>{{ groups.length }}</b> groups have read access to this project.
                            </p>
                        </info-card>
                    </v-col>
                </v-row>
                <v-card-text class="mt-10">
                    <v-row>
                        <span class="text-h5 black--text">Manage group</span>
                        <v-spacer />
                        <v-btn
                            v-if="props.project.has_groups"
                            variant="text"
                            size="small"
                            class="text-none text-body-2 mr-1"
                            color="primary"
                            @click="router.visit(route('projects.groups.create', {project: props.project.slug}))"
                        >
                            Create group
                        </v-btn>

                        <success-btn
                            size="small"
                            class="mr-1"
                            @click="router.visit(route('projects.users.index', {project: props.project.slug}))"
                        >
                            Manage access
                        </success-btn>
                    </v-row>
                </v-card-text>
            </v-card>
            <groups-table
                :project="props.project"
                :groups="props.groups"
                @click="addMember($event)"
            />
        </v-container>
    </project-layout>
</template>

<style scoped>
</style>
